const EVENTS_KEY = 'mojopayr_merchant_events'
const POS_KEY = 'mojopayr_merchant_pos_requests'
const TAX_KEY = 'mojopayr_merchant_tax_remittances'
const INVOICES_KEY = 'mojopayr_merchant_invoices'
const ATTENDANCE_KEY = 'mojopayr_event_attendance'

function readList(key) {
    try {
        const raw = localStorage.getItem(key)
        const parsed = raw ? JSON.parse(raw) : []
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

function writeList(key, items) {
    localStorage.setItem(key, JSON.stringify(items))
}

function buildId(prefix) {
    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

export function createMerchantEvent(payload) {
    const events = readList(EVENTS_KEY)
    const record = {
        id: buildId('evt'),
        createdAt: new Date().toISOString(),
        status: 'draft',
        ...payload,
    }
    const next = [record, ...events]
    writeList(EVENTS_KEY, next)
    return record
}

export function getMerchantEvents() {
    return readList(EVENTS_KEY)
}

export function createPosRequest(payload) {
    const requests = readList(POS_KEY)
    const amount = Number(payload.amount || 0)
    const fee = Number((amount * 0.0075).toFixed(2))
    const record = {
        id: buildId('pos'),
        reference: `MP-${Date.now().toString().slice(-8)}`,
        createdAt: new Date().toISOString(),
        status: 'presented',
        amount,
        fee,
        total: Number((amount + fee).toFixed(2)),
        ...payload,
    }
    const next = [record, ...requests]
    writeList(POS_KEY, next)
    return record
}

export function getPosRequests() {
    return readList(POS_KEY)
}

export function createMerchantTaxRemittance(payload) {
    const remittances = readList(TAX_KEY)
    const amount = Number(payload.amount || 0)
    const record = {
        id: buildId('tax'),
        receiptNumber: `TAX-${Date.now().toString().slice(-8)}`,
        createdAt: new Date().toISOString(),
        status: 'receipted',
        amount,
        ...payload,
    }
    const next = [record, ...remittances]
    writeList(TAX_KEY, next)
    return record
}

export function getMerchantTaxRemittances() {
    return readList(TAX_KEY)
}

export function createMerchantInvoiceRecord(payload) {
    const invoices = readList(INVOICES_KEY)
    const amount = Number(payload.amount || 0)
    const record = {
        id: buildId('inv'),
        invoiceCode: `INV-${Date.now().toString().slice(-5)}`,
        paymentRef: `MP-${Date.now().toString().slice(-10)}`,
        createdAt: new Date().toISOString(),
        status: 'sent',
        amount,
        total: amount,
        ...payload,
    }
    const next = [record, ...invoices]
    writeList(INVOICES_KEY, next)
    return record
}

export function getMerchantInvoices() {
    return readList(INVOICES_KEY)
}

export function getMerchantInvoiceById(invoiceId) {
    return readList(INVOICES_KEY).find((item) => item.id === invoiceId) || null
}

export function updateMerchantInvoice(invoiceId, patch) {
    const invoices = readList(INVOICES_KEY)
    const next = invoices.map((item) => item.id === invoiceId ? { ...item, ...patch } : item)
    writeList(INVOICES_KEY, next)
    return next.find((item) => item.id === invoiceId) || null
}

export function getEventAttendance(eventId) {
    return readList(ATTENDANCE_KEY).filter((item) => item.eventId === eventId)
}

export function getEventGateCounts(eventId) {
    const rows = getEventAttendance(eventId)
    return ['Gate A', 'Gate B', 'Gate C', 'Gate D'].reduce((counts, gateName) => {
        counts[gateName] = rows.filter((item) => item.gateName === gateName).length
        return counts
    }, {})
}

export function validateMerchantTicket({ eventId, gateName, ticketCode, staffName }) {
    const normalizedCode = String(ticketCode || '').trim().toUpperCase()
    if (!eventId || !gateName || !normalizedCode) {
        return {
            tone: 'warning',
            title: 'Missing ticket details',
            body: 'Choose an event, gate, and ticket code before validation.',
        }
    }

    const attendance = readList(ATTENDANCE_KEY)
    const duplicate = attendance.find(
        (item) => item.eventId === eventId && item.ticketCode === normalizedCode
    )

    if (duplicate) {
        return {
            tone: 'error',
            title: 'Ticket already scanned',
            body: `${normalizedCode} was already checked in at ${duplicate.gateName}.`,
        }
    }

    const record = {
        id: buildId('att'),
        eventId,
        gateName,
        ticketCode: normalizedCode,
        staffName: String(staffName || '').trim(),
        checkedAt: new Date().toISOString(),
    }

    writeList(ATTENDANCE_KEY, [record, ...attendance])

    return {
        tone: 'success',
        title: 'Ticket accepted',
        body: `${normalizedCode} checked in at ${gateName}.`,
        record,
    }
}
